
import { supabase } from '../supabase';
import { logger } from '../utils/logger';

export interface Service {
    id: string;
    title: string;
    slug: string;
    short_description: string;
    full_content?: string;
    icon_class?: string;
    image_url?: string;
    display_order: number;
    is_active: boolean;
}

export interface FAQ {
    id: string;
    question: string;
    answer: string;
    category: string;
    display_order: number;
}

export interface NewsArticle {
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    content?: string;
    cover_image_url?: string;
    author: string;
    published_at: string;
    is_published: boolean;
    pdf_url?: string;
    pdf_name?: string;
}

export interface Ethos {
    id: string;
    type: 'mission' | 'vision' | 'values';
    title: string;
    content: string;
}

export interface SiteSetting {
    id: string;
    setting_key: string;
    setting_value: string;
    setting_group: string;
}

export const cmsService = {
    // Services
    async getServices(): Promise<Service[]> {
        const { data, error } = await supabase
            .from('services')
            .select('*')
            .order('display_order', { ascending: true });

        if (error) {
            logger.error('Error fetching services', error);
            return [];
        }
        return data || [];
    },

    async createService(service: Omit<Service, 'id'>) {
        const { data, error } = await supabase
            .from('services')
            .insert(service)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async updateService(id: string, updates: Partial<Service>) {
        const { data, error } = await supabase
            .from('services')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async deleteService(id: string) {
        const { error } = await supabase
            .from('services')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // FAQs
    async getFAQs(): Promise<FAQ[]> {
        const { data, error } = await supabase
            .from('faqs')
            .select('*')
            .order('display_order', { ascending: true });

        if (error) {
            logger.error('Error fetching FAQs', error);
            return [];
        }
        return data || [];
    },

    async createFAQ(faq: Omit<FAQ, 'id'>) {
        const { data, error } = await supabase
            .from('faqs')
            .insert(faq)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async updateFAQ(id: string, updates: Partial<FAQ>) {
        const { data, error } = await supabase
            .from('faqs')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async deleteFAQ(id: string) {
        const { error } = await supabase
            .from('faqs')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // News
    async getNewsArticles(): Promise<NewsArticle[]> {
        const { data, error } = await supabase
            .from('news_articles')
            .select('*')
            .order('published_at', { ascending: false });

        if (error) {
            logger.error('Error fetching news', error);
            return [];
        }
        return data || [];
    },

    async getNewsArticleBySlug(slug: string): Promise<NewsArticle | null> {
        const decodedSlug = decodeURIComponent(slug);
        const { data, error } = await supabase
            .from('news_articles')
            .select('*')
            .eq('slug', decodedSlug)
            .single();

        if (error) {
            if (error.code !== 'PGRST116') { // Ignore "no rows found" error logic
                console.error('Error fetching news article:', error);
            }
            return null;
        }
        return data;
    },

    async createNewsArticle(article: Omit<NewsArticle, 'id'>) {
        const { data, error } = await supabase
            .from('news_articles')
            .insert(article)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async updateNewsArticle(id: string, updates: Partial<NewsArticle>) {
        const { data, error } = await supabase
            .from('news_articles')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async deleteNewsArticle(id: string) {
        const { error } = await supabase
            .from('news_articles')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // Ethos
    async getCompanyEthos(): Promise<Ethos[]> {
        const { data, error } = await supabase
            .from('company_ethos')
            .select('*');

        if (error) {
            logger.error('Error fetching ethos', error);
            return [];
        }
        return data || [];
    },

    async updateCompanyEthos(id: string, content: string) {
        const { data, error } = await supabase
            .from('company_ethos')
            .update({ content })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // Site Settings
    async getSiteSettings(): Promise<SiteSetting[]> {
        const { data, error } = await supabase
            .from('site_settings')
            .select('*');

        if (error) {
            logger.error('Error fetching settings', error);
            return [];
        }
        return data || [];
    },

    async updateSiteSetting(key: string, value: string) {
        const { error } = await supabase
            .from('site_settings')
            .upsert({ setting_key: key, setting_value: value }, { onConflict: 'setting_key' });

        if (error) {
            logger.error('Error updating setting', error);
            throw error;
        }
    },

    // Stats
    async getStats() {
        const { count: services } = await supabase
            .from('services')
            .select('*', { count: 'exact', head: true });

        const { count: faqs } = await supabase
            .from('faqs')
            .select('*', { count: 'exact', head: true });

        const { count: news } = await supabase
            .from('news_articles')
            .select('*', { count: 'exact', head: true });

        return {
            services: services || 0,
            faqs: faqs || 0,
            news: news || 0
        };
    },

    // Storage
    async uploadImage(file: File): Promise<string> {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('news-images')
            .upload(filePath, file);

        if (uploadError) {
            throw uploadError;
        }

        const { data } = supabase.storage
            .from('news-images')
            .getPublicUrl(filePath);

        return data.publicUrl;
    },

    async uploadDocument(file: File): Promise<string> {
        const fileExt = file.name.split('.').pop();
        // create a safe file name using regex
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `${Date.now()}_${safeName}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('news-documents')
            .upload(filePath, file);

        if (uploadError) {
            throw uploadError;
        }

        const { data } = supabase.storage
            .from('news-documents')
            .getPublicUrl(filePath);

        return data.publicUrl;
    }
};
